import React, { useEffect, useMemo, useState } from 'react';
import {
    Badge,
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    IconButton,
    Sheet,
    Table,
    Typography,
} from '@mui/joy';
import {
    CheckCircle,
    CloudUpload,
    Error as ErrorIcon,
    HourglassEmpty,
    Info,
    KeyboardArrowLeft,
    KeyboardArrowRight,
    Warning,
} from '@mui/icons-material';
import { formatRemaining } from './UploadJobStatusChip';
import { resolveSessionEndsAt } from '../../hooks/useUploadRequest';

const STATUS_META = {
    pending: { color: 'neutral', label: 'Waiting for hub', icon: <HourglassEmpty /> },
    in_progress: { color: 'primary', label: 'Uploading', icon: <Info /> },
    collecting: { color: 'primary', label: 'Collecting', icon: <Info /> },
    uploading: { color: 'primary', label: 'Uploading', icon: <Info /> },
    completed: { color: 'success', label: 'Completed', icon: <CheckCircle /> },
    partial: { color: 'warning', label: 'Partial', icon: <Warning /> },
    failed: { color: 'danger', label: 'Failed', icon: <ErrorIcon /> },
    expired: { color: 'danger', label: 'Expired', icon: <ErrorIcon /> },
    cancelled: { color: 'neutral', label: 'Cancelled', icon: <Info /> },
};

function typeLabel(job) {
    return job?.type === 'collection_session' ? 'Collect then upload' : 'Upload existing';
}

function formatSources(sources) {
    if (!Array.isArray(sources) || sources.length === 0) return '—';
    return sources.map((source) => source.replace(/_/g, ' ')).join(', ');
}

function formatWindow(job) {
    if (job?.type === 'collection_session') {
        const duration = Number(job.duration_ms);
        const durationLabel = Number.isFinite(duration) && duration > 0
            ? formatRemaining(duration)
            : null;
        const endsAt = resolveSessionEndsAt(job);
        if (job.status === 'collecting' && endsAt != null) {
            return durationLabel
                ? `${durationLabel} · ends ${new Date(endsAt).toLocaleString()}`
                : `Ends ${new Date(endsAt).toLocaleString()}`;
        }
        return durationLabel ? `Duration ${durationLabel}` : '—';
    }
    const from = job?.from_ms != null ? new Date(job.from_ms).toLocaleString() : null;
    const to = job?.to_ms != null ? new Date(job.to_ms).toLocaleString() : null;
    if (from && to) return `${from} → ${to}`;
    return '—';
}

function formatCountMap(map) {
    if (!map || typeof map !== 'object') return null;
    const parts = Object.entries(map)
        .filter(([, value]) => value != null)
        .map(([key, value]) => `${key.replace(/_/g, ' ')}: ${value}`);
    return parts.length ? parts.join(' · ') : null;
}

function formatResult(job) {
    const rows = formatCountMap(job?.rows_uploaded);
    const expired = formatCountMap(job?.rows_expired_locally);
    const error = typeof job?.error === 'string' ? job.error : job?.error?.message;
    const parts = [];
    if (rows) parts.push(rows);
    if (job?.status === 'partial' && expired) parts.push(`Expired locally: ${expired}`);
    if (error) parts.push(error);
    return parts.length ? parts.join(' · ') : '—';
}

function StatusCell({ job }) {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (job?.status !== 'collecting') return undefined;
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [job?.status]);

    const meta = STATUS_META[job?.status] || {
        color: 'neutral',
        label: job?.status || 'Unknown',
        icon: <Info />,
    };
    let label = meta.label;
    if (job?.status === 'collecting') {
        const endsAt = resolveSessionEndsAt(job);
        if (endsAt != null) {
            const remaining = endsAt - now;
            label = remaining <= 0
                ? 'Collecting · ending now'
                : `Collecting · ${formatRemaining(remaining)}`;
        }
    }

    return (
        <Chip size="sm" variant="soft" color={meta.color} startDecorator={meta.icon}>
            {label}
        </Chip>
    );
}

const PAGE_SIZE = 10;

export default function UploadRequestsSection({
    jobs,
    loading,
    error,
    onNewRequest,
    onRequestAgain,
}) {
    const items = Array.isArray(jobs) ? jobs : [];
    const [page, setPage] = useState(0);

    const pageCount = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
    const safePage = Math.min(page, pageCount - 1);
    const pageItems = useMemo(() => {
        const start = safePage * PAGE_SIZE;
        return items.slice(start, start + PAGE_SIZE);
    }, [items, safePage]);

    const newestId = items[0]?.job_id;
    useEffect(() => {
        setPage(0);
    }, [newestId]);

    useEffect(() => {
        if (page !== safePage) setPage(safePage);
    }, [page, safePage]);

    const rangeStart = items.length === 0 ? 0 : safePage * PAGE_SIZE + 1;
    const rangeEnd = Math.min(items.length, (safePage + 1) * PAGE_SIZE);

    return (
        <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                <Typography level="h3" sx={{ display: 'flex', alignItems: 'center' }}>
                    <CloudUpload sx={{ mr: 1 }} />
                    Data requests
                    <Badge badgeContent={items.length} color="primary" sx={{ ml: 2 }}>
                        <Box />
                    </Badge>
                </Typography>
                <Button
                    size="sm"
                    variant="solid"
                    startDecorator={<CloudUpload />}
                    onClick={() => onNewRequest()}
                >
                    New request
                </Button>
            </Box>

            {error && (
                <Typography level="body-sm" color="danger" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}

            {loading && items.length === 0 ? (
                <Card variant="soft" sx={{ p: 3, textAlign: 'center' }}>
                    <CircularProgress size="sm" sx={{ mb: 1 }} />
                    <Typography level="body-sm" textColor="neutral.500">
                        Loading requests…
                    </Typography>
                </Card>
            ) : items.length === 0 ? (
                <Card variant="soft" sx={{ p: 3, textAlign: 'center' }}>
                    <Info sx={{ fontSize: 48, color: 'neutral.400', mb: 2 }} />
                    <Typography level="body-sm" textColor="neutral.500">
                        No data requests yet
                    </Typography>
                </Card>
            ) : (
                <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'hidden' }}>
                    <Box sx={{ overflow: 'auto' }}>
                        <Table hoverRow stickyHeader size="sm" sx={{ minWidth: 880 }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 160 }}>Created</th>
                                    <th style={{ width: 150 }}>Type</th>
                                    <th style={{ width: 180 }}>Status</th>
                                    <th style={{ width: 180 }}>Sources</th>
                                    <th>Window</th>
                                    <th>Result</th>
                                    <th style={{ width: 88 }} />
                                </tr>
                            </thead>
                            <tbody>
                                {pageItems.map((item) => {
                                    const canRetry = item.status === 'expired' || item.status === 'failed';
                                    return (
                                        <tr key={item.job_id}>
                                            <td>
                                                <Typography level="body-sm">
                                                    {item.created_at ? new Date(item.created_at).toLocaleString() : '—'}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-sm">{typeLabel(item)}</Typography>
                                            </td>
                                            <td>
                                                <StatusCell job={item} />
                                            </td>
                                            <td>
                                                <Typography level="body-sm">{formatSources(item.sources)}</Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-sm">{formatWindow(item)}</Typography>
                                            </td>
                                            <td>
                                                <Typography
                                                    level="body-sm"
                                                    color={item.status === 'failed' || item.status === 'expired' ? 'danger' : undefined}
                                                >
                                                    {formatResult(item)}
                                                </Typography>
                                            </td>
                                            <td>
                                                {canRetry && typeof onRequestAgain === 'function' && (
                                                    <Button
                                                        size="sm"
                                                        variant="plain"
                                                        onClick={() => onRequestAgain(item)}
                                                    >
                                                        Retry
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 1,
                            px: 1.5,
                            py: 1,
                            borderTop: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Typography level="body-sm" textColor="neutral.500">
                            {rangeStart}–{rangeEnd} of {items.length}
                        </Typography>
                        <IconButton
                            size="sm"
                            variant="plain"
                            disabled={safePage === 0}
                            onClick={() => setPage((current) => Math.max(0, current - 1))}
                            aria-label="Previous page"
                        >
                            <KeyboardArrowLeft />
                        </IconButton>
                        <IconButton
                            size="sm"
                            variant="plain"
                            disabled={safePage >= pageCount - 1}
                            onClick={() => setPage((current) => Math.min(pageCount - 1, current + 1))}
                            aria-label="Next page"
                        >
                            <KeyboardArrowRight />
                        </IconButton>
                    </Box>
                </Sheet>
            )}
        </Box>
    );
}
